import {
    Image, Ingredient, IngredientRecipeSubgraphResult, IngredientUpdate, IngredientWithUsage,
    MergeRequest, RecipeListItem, TransformationSuggestion,
} from './types';
import { authService } from '../auth/AuthService.js';

export async function getIngredient(ingredientId: string): Promise<Ingredient> {
    const response = await fetch(`/api/ingredient/${ingredientId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function createIngredient(ingredient: Omit<Ingredient, 'id'>): Promise<Ingredient> {
    const response = await fetch('/api/ingredient/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(ingredient),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function updateIngredient(ingredientId: string, ingredientUpdate: IngredientUpdate): Promise<Ingredient> {
    const response = await fetch(`/api/ingredient/${ingredientId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(ingredientUpdate),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to update ingredient' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
}

export async function searchIngredients(query: string, limit: number = 20): Promise<Ingredient[]> {
    const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
    });
    const response = await fetch(`/api/ingredients/search?${params}`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}


export async function addIngredientImage(ingredientId: string, file: Blob): Promise<Image> {
    const formData = new FormData();
    formData.append('file', file, 'image.jpg');
    const response = await fetch(`/api/ingredient/${ingredientId}/images`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders() },
        body: formData,
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to upload image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteIngredientImage(ingredientId: string, imageId: string): Promise<void> {
    const response = await fetch(`/api/ingredient/${ingredientId}/images/${imageId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

export async function setDefaultIngredientImage(ingredientId: string, imageId: string): Promise<Ingredient> {
    const response = await fetch(`/api/ingredient/${ingredientId}/images/${imageId}/default`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to set default image' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function getIngredientRecipeSubgraphs(
    ingredientId: string
): Promise<IngredientRecipeSubgraphResult[]> {
    const response = await fetch(`/api/ingredient/${ingredientId}/recipe-subgraphs`);
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch ingredient recipes' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Every ingredient plus its recipe-usage count — for the explorer's table. */
export async function listIngredients(): Promise<IngredientWithUsage[]> {
    const response = await fetch('/api/ingredients');
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function deleteIngredient(ingredientId: string): Promise<void> {
    const response = await fetch(`/api/ingredient/${ingredientId}`, {
        method: 'DELETE',
        headers: { ...authService.getAuthHeaders() },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to delete ingredient' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
}

/** Merge one or more ingredients into mergeRequest.targetId. */
export async function mergeIngredients(mergeRequest: MergeRequest): Promise<Ingredient> {
    const response = await fetch('/api/ingredients/merge', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
        },
        body: JSON.stringify(mergeRequest),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to merge ingredients' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Recipes that use this ingredient anywhere in their graph. */
export async function getRecipesUsingIngredient(ingredientId: string): Promise<RecipeListItem[]> {
    const response = await fetch(`/api/ingredient/${ingredientId}/recipes`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

/** Recipes that show how this ingredient itself is prepared (the "Afegeix
 *  elaboració" side, as opposed to getRecipesUsingIngredient's plain uses). */
export async function getRecipesElaboratingIngredient(ingredientId: string): Promise<RecipeListItem[]> {
    const response = await fetch(`/api/ingredient/${ingredientId}/elaborations`);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
}

export async function getTransformationSuggestions(
    ingredientIds: string[]
): Promise<TransformationSuggestion[]> {
    const params = new URLSearchParams();
    for (const id of ingredientIds) {
        params.append('ingredient_id', id);
    }
    const response = await fetch(`/api/ingredients/suggest-transformations?${params}`);
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch transformation suggestions' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
}
